import {
  Box,
  Button,
  Grid2,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import React from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { updateActivity } from "../../api/activity";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { Activity } from "../../types/model/activity";
import { UpdateActivityRequestSchema } from "../../types/model/activity/UpdateActivityRequest";

interface ActivityDetailFormProps {
  accountId: number;
  projectId: number;
  activity: Activity | undefined;
  onUpdate?: (activity: Activity) => void;
  readOnly: boolean;
}

const ActivityDetailForm = ({
  accountId,
  projectId,
  activity,
  onUpdate,
  readOnly
}: ActivityDetailFormProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const [editing, setEditing] = React.useState(false);
  const formStyles = useFormStyles();

  const handleUpdateActivity = async (values: any) => {
    try {
      const request = UpdateActivityRequestSchema.parse({
        name: values.name,
        description: values.description,
        startDate: values.startDate,
        endDate: values.endDate,
        accountId,
        ownerId: activity?.ownerId,
      });
      const response = await updateActivity(projectId, activity?.id!, request);
      onUpdate?.(response);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
      setEditing(false);
    }
  };
  
  return (
    <Box>
      <Formik
        initialValues={{
          name: activity?.name ?? "",
          description: activity?.description ?? "",
          startDate: activity?.startDate ?? null,
          endDate: activity?.endDate ?? null,
        }}
        onSubmit={(values) => {
          dispatch(setLoading(true));
          handleUpdateActivity(values);
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required(t("activities.name-required")),
          description: Yup.string().required(t("activities.description-required")),
          startDate: Yup.date()
            .nullable()
            .typeError(t("admin.projects.startDate-valid")),
          endDate: Yup.date()
            .nullable()
            .typeError(t("admin.projects.endDate-valid"))
            .when("startDate", (startDate, schema) =>
              startDate?.[0]
                ? schema.min(startDate[0], t("admin.projects.endDate-after-startDate"))
                : schema
            ),
        })}
      >
        {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={12}>
                <TextField
                  fullWidth
                  required
                  variant={editing ? "outlined" : "standard"}
                  disabled={!editing}
                  sx={formStyles}
                  id="name"
                  label={t("activities.name")}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.name && touched.name)}
                  helperText={errors.name || ""}
                />
              </Grid2>

              <Grid2 size={12}>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                  {t("projects.dates")}
                </Typography>

                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <DatePicker
                      label={t("activities.start-date")}
                      disabled={!editing}
                      value={values.startDate ? moment(values.startDate) : null}
                      onChange={(value) =>
                        setFieldValue("startDate", value ? value.toDate() : null)
                      }
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          fullWidth: true,
                          variant: editing ? "outlined" : "standard",
                          error: !!(errors.startDate && touched.startDate),
                          helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                          sx: formStyles,
                        },
                      }}
                      format="DD/MM/YYYY"
                      views={["day", "month", "year"]}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <DatePicker
                      label={t("activities.end-date")}
                      disabled={!editing}
                      value={values.endDate ? moment(values.endDate) : null}
                      onChange={(value) =>
                        setFieldValue("endDate", value ? value.toDate() : null)
                      }
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          fullWidth: true,
                          variant: editing ? "outlined" : "standard",
                          error: !!(errors.endDate && touched.endDate),
                          helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                          sx: formStyles,
                        },
                      }}
                      format="DD/MM/YYYY"
                      views={["day", "month", "year"]}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              <Grid2 size={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  disabled={!editing}
                  variant={editing ? "outlined" : "standard"}
                  id="description"
                  label={t("activities.description")}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.description && touched.description)}
                  helperText={touched.description && errors.description}
                  sx={formStyles}
                />
              </Grid2>

              {/* Ações */}
              {!readOnly && (
                <Grid2
                  size={12}
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
                >
                  {editing ? (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setEditing(false)}
                      >
                        {t("generic.cancel")}
                      </Button>
                      <Button type="submit" variant="contained">
                        {t("generic.save")}
                      </Button>
                    </>
                  ) : (
                    <Button variant="contained" onClick={() => setEditing(true)}>
                      {t("generic.edit")}
                    </Button>
                  )}
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default ActivityDetailForm;